import { useState } from 'react';

import type { ResizableColumnsWidth } from './ResizableColumns';

/**
 * The pointer and keyboard mechanics of a divider, and the width arithmetic that goes with them.
 *
 * **A drag moves width between the two columns it sits between, and nowhere else.** Their sum is
 * held constant, so every other column keeps the width it had and nothing shifts under the reader's
 * hand. It is also what makes the sum meaningful as a span to clamp against: 0 collapses the column
 * on the left, the whole span collapses the one on the right.
 */
export const useResizableColumnsDrag = ({
  onChange,
  onCommit,
  widths,
}: {
  /** Called for every move of a drag, so the columns follow the pointer. */
  onChange: (widths: ResizableColumnsWidth[]) => void;
  /** Called once, when the drag ends - the point at which a caller would persist the layout. */
  onCommit: (widths: ResizableColumnsWidth[]) => void;
  widths: ResizableColumnsWidth[];
}) => {
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

  /*
    **A fixed track has no share to give.** Width moves from one column to the other, which is
    arithmetic on two ratios - a column measured in pixels is not one of those, so the pair it
    belongs to cannot be dragged at all. `ResizableColumns` draws no handle there; this is what
    makes that a fact rather than a convention the caller could get past.
  */
  const shares = ({ index }: { index: number }) => {
    const before = widths[index];
    const after = widths[index + 1];

    return typeof before === 'number' && typeof after === 'number'
      ? { after, before }
      : null;
  };

  /*
    Width moved from one column to the other, as a new array. The pair's total is preserved by
    construction: whatever the left column takes, the right one gives up.
  */
  const resized = ({
    index,
    nextBefore,
    span,
  }: {
    index: number;
    nextBefore: number;
    span: number;
  }) => {
    const clamped = Math.min(Math.max(nextBefore, 0), span);

    return widths.map((width, position) => {
      if (position === index) return clamped;
      return position === index + 1 ? span - clamped : width;
    });
  };

  const startDrag = ({
    event,
    index,
  }: {
    event: React.PointerEvent<HTMLElement>;
    index: number;
  }) => {
    /*
      A second pointer landing on a divider that is already being dragged would stack a second set
      of listeners on it, and an end handler removes only the closures it was made alongside - so a
      single release would commit twice.
    */
    if (draggingIndex !== null) return;

    const handle = event.currentTarget;
    const pair = shares({ index });

    /*
      **The two columns are read off the DOM rather than from refs.** A handle always sits between
      the two tracks it resizes, so its siblings *are* those columns - which needs no ref plumbing
      and cannot fall out of step with the rendered order.
    */
    const beforeElement = handle.previousElementSibling;
    const afterElement = handle.nextElementSibling;

    if (
      !pair ||
      !(beforeElement instanceof HTMLElement) ||
      !(afterElement instanceof HTMLElement)
    ) {
      return;
    }

    const span = pair.before + pair.after;
    const measured =
      beforeElement.getBoundingClientRect().width +
      afterElement.getBoundingClientRect().width;

    /*
      How much width one pixel of travel is worth, measured from the pair rather than assumed. It
      has to be measured because `fr` is a ratio: the same value is a different number of pixels in
      a narrow container than a wide one, and the handles and any gap have already been taken out of
      what the columns share.

      **The fallback is for a pair that is entirely collapsed.** Both measure 0, so the ratio would
      be worthless - the grid's own width stands in, which is the widest the pair could ever be and
      so errs towards a slower drag rather than a runaway one.
    */
    const basis =
      measured || handle.parentElement?.getBoundingClientRect().width;

    if (!basis) return;

    const startX = event.clientX;
    const widthPerPixel = span / basis;

    /*
      Every move is measured from where the drag started rather than from the last move, so rounding
      cannot accumulate over a long drag and the column cannot creep away from the pointer.
    */
    let latest: ResizableColumnsWidth[] = widths;

    const onPointerMove = (moveEvent: PointerEvent) => {
      latest = resized({
        index,
        nextBefore: pair.before + (moveEvent.clientX - startX) * widthPerPixel,
        span,
      });
      onChange(latest);
    };

    const onPointerEnd = () => {
      handle.removeEventListener('pointermove', onPointerMove);
      handle.removeEventListener('pointerup', onPointerEnd);
      handle.removeEventListener('pointercancel', onPointerEnd);
      setDraggingIndex(null);
      onCommit(latest);
    };

    /*
      Capture, so a pointer that outruns the handle keeps feeding it - without this the drag stops
      the moment the pointer crosses into a column. It also retargets the moves to the handle, which
      is why the listeners below go on the handle and not the window.
    */
    handle.setPointerCapture(event.pointerId);
    handle.addEventListener('pointermove', onPointerMove);
    handle.addEventListener('pointerup', onPointerEnd);
    handle.addEventListener('pointercancel', onPointerEnd);
    setDraggingIndex(index);
  };

  /*
    A step is a twentieth of the pair rather than a fixed number of pixels, so the arrow keys cover
    a divider's whole range in the same number of presses whatever the container's width.
  */
  const nudge = ({
    direction,
    index,
  }: {
    direction: -1 | 1;
    index: number;
  }) => {
    const pair = shares({ index });

    if (!pair) return;

    const span = pair.before + pair.after;
    const next = resized({
      index,
      nextBefore: pair.before + (direction * span) / 20,
      span,
    });

    onChange(next);
    onCommit(next);
  };

  return { draggingIndex, nudge, startDrag };
};
